import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Loader2, Flame, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { dashApi } from "../api";
import {
  KANBAN_STAGES, STAGE_LABEL, STAGE_BADGE,
  formatFormType, formatRelative,
  LEAD_TYPE_LABEL, LEAD_TYPE_BADGE,
} from "../utils";
import SLABubble from "./SLABubble";

const formatMoney = (n) => {
  if (n === null || n === undefined) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${n}`;
};

const STAGE_COLUMN_TINT = {
  new:         "bg-blue-50/70",
  contacted:   "bg-violet-50/70",
  qualified:   "bg-amber-50/70",
  quote_sent:  "bg-cyan-50/70",
  negotiation: "bg-orange-50/70",
  won:         "bg-emerald-50/70",
  lost:        "bg-rose-50/70",
};

const Card_ = ({ card, index, isAdminLike: _x }) => (
  <Draggable draggableId={card.id} index={index}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        data-testid={`pipeline-card-${card.id}`}
        className={`bg-white border rounded-md p-3 mb-2 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing ${
          snapshot.isDragging ? "ring-2 ring-blue-400 shadow-lg" : "border-slate-200"
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <div className="font-medium text-sm text-slate-900 truncate">
                {card.name}
              </div>
              {card.is_hot && (
                <Flame className="h-3.5 w-3.5 text-rose-500 shrink-0" title={card.hot_reason} />
              )}
            </div>
            {card.company && (
              <div className="text-xs text-slate-500 truncate">{card.company}</div>
            )}
          </div>
          <Link
            to={`/dashboard/submissions/${card.id}`}
            className="text-slate-400 hover:text-slate-700 shrink-0"
            title="Open submission"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="text-slate-500">{formatFormType(card.formType)}</span>
          <span className="font-medium text-slate-800">{formatMoney(card.estimated_value)}</span>
        </div>
        {card.lead_type && card.lead_type !== "new_prospect" && (
          <div className="mt-1.5">
            <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full border ${LEAD_TYPE_BADGE[card.lead_type]}`} data-testid={`card-lead-type-${card.id}`}>
              {LEAD_TYPE_LABEL[card.lead_type]}
            </span>
          </div>
        )}
        <div className="mt-1 flex items-center justify-between text-[11px] text-slate-400">
          <span>{formatRelative(card.created_at)}</span>
          <div className="flex items-center gap-1.5">
            {card.sla_state && (
              <SLABubble state={card.sla_state} dueIso={card.follow_up_due} compact testid={`card-sla-${card.id}`} />
            )}
            {card.assigned_to_name && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700"
                title={`Assigned to ${card.assigned_to_name}`}
                data-testid={`assignee-chip-${card.id}`}
              >
                <span className="h-3.5 w-3.5 rounded-full bg-slate-300 text-[9px] font-bold flex items-center justify-center text-white">
                  {card.assigned_to_name.trim().slice(0, 1).toUpperCase()}
                </span>
                {card.assigned_to_name.split(" ")[0]}
              </span>
            )}
          </div>
        </div>
      </div>
    )}
  </Draggable>
);

export default function PipelineBoard({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await dashApi.get("/pipeline");
      setData(data);
    } catch (e) {
      setErr(e.message || "Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, source, destination } = result;
    if (source.droppableId === destination.droppableId &&
        source.index === destination.index) return;

    const toStage = destination.droppableId;
    const fromStage = source.droppableId;

    // Spam needs a reason — block direct drag and steer the user to detail page.
    if (toStage === "spam") {
      toast.info("Open the lead to mark it Spam (a reason is required).");
      return;
    }
    // Optimistic update
    setData((prev) => {
      if (!prev) return prev;
      const cols = { ...prev.columns };
      const srcList = Array.from(cols[fromStage] || []);
      const [moved] = srcList.splice(source.index, 1);
      if (!moved) return prev;
      moved.status = toStage;
      const dstList = Array.from(cols[toStage] || []);
      dstList.splice(destination.index, 0, moved);
      cols[fromStage] = srcList;
      cols[toStage] = dstList;
      // recompute totals
      const totals = {};
      for (const s of KANBAN_STAGES) {
        totals[s] = (cols[s] || []).reduce((a, c) => a + (c.estimated_value || 0), 0);
      }
      return { ...prev, columns: cols, stage_totals: totals };
    });

    try {
      await dashApi.patch(`/submissions/${draggableId}/stage`, { status: toStage });
      toast.success(`Moved to ${STAGE_LABEL[toStage] || toStage}`);
    } catch (e) {
      toast.error(e.message || "Failed to move card");
      load(); // revert by reloading
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading pipeline…
      </div>
    );
  }
  if (err) {
    return <div className="text-rose-700 bg-rose-50 border border-rose-200 p-4 rounded-md">{err}</div>;
  }

  const cols = data.columns || {};
  const totals = data.stage_totals || {};

  return (
    <div data-testid="pipeline-board">
      {!compact && (
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-sm text-slate-600">
            <span className="font-medium text-slate-900">Pipeline value:</span>{" "}
            <span data-testid="pipeline-total-value">{formatMoney(data.total_pipeline_value)}</span>
            <span className="mx-2 text-slate-300">•</span>
            <span className="font-medium text-slate-900">Won:</span>{" "}
            <span data-testid="pipeline-won-value">{formatMoney(data.won_value)}</span>
          </div>
        </div>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-3">
          {KANBAN_STAGES.map((stage) => {
            const list = cols[stage] || [];
            return (
              <div
                key={stage}
                className={`flex-shrink-0 w-72 rounded-lg border border-slate-200 ${STAGE_COLUMN_TINT[stage] || "bg-slate-50"}`}
                data-testid={`pipeline-column-${stage}`}
              >
                <div className="px-3 py-2 border-b border-slate-200/70 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border ${STAGE_BADGE[stage] || "bg-slate-100"}`}>
                      {STAGE_LABEL[stage] || stage}
                    </span>
                    <span className="text-xs text-slate-500">{list.length}</span>
                  </div>
                  <span className="text-xs font-medium text-slate-700">
                    {formatMoney(totals[stage] || 0)}
                  </span>
                </div>
                <Droppable droppableId={stage}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`p-2 min-h-[140px] ${compact ? "max-h-72" : "max-h-[600px]"} overflow-y-auto transition-colors ${
                        snapshot.isDraggingOver ? "bg-blue-50/40" : ""
                      }`}
                    >
                      {list.length === 0 ? (
                        <div className="text-center text-xs text-slate-400 italic py-6">
                          No leads
                        </div>
                      ) : (
                        list.map((card, i) => (
                          <Card_ key={card.id} card={card} index={i} />
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}
